import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Link,
  CircularProgress,
  Button,
} from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { Navigate, useNavigate } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { BETA_FOLDERS_FEATURE_ENABLED } from '@/features/betaProjects/config';
import { SECOND_BRAIN_FEATURE_ENABLED } from '@/features/secondBrain/config';
import { VIBE_CODE_FEATURE_ENABLED } from '@/features/vibeCode/config';
import { ZAPIER_FEATURE_ENABLED } from '@/features/zapier/config';
import { SMART_TAGS_FEATURE_ENABLED } from '@/features/betaSmartTags/config';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';
import WebhookIcon from '@mui/icons-material/Webhook';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { hasBetaFeature, isBetaUser } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';

interface BetaFeature {
  id: string;
  title: string;
  description: string;
  steps: string[];
  actionLabel: string;
  route: string;
  // Set for features that live inside another screen rather than having their own page
  location?: string;
  icon: React.ReactNode;
  status: 'active' | 'coming-soon';
}

const Beta = () => {
  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const { betaFeatures: planBetaFeatures, isLoading: isPlanLoading } = useCurrentPlanFeatures();

  // Check if user has Beta Tester plan access or plan-level beta features
  const isBetaTester = isBetaUser(userData, planBetaFeatures);

  // Don't redirect while pricing data is still loading — betaFeatures may not be available yet
  if (!isBetaTester && !isPlanLoading) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  const betaFeatures: BetaFeature[] = [];

  if (planBetaFeatures.customUploads) {
    betaFeatures.push({
      id: 'video-upload',
      title: 'Video Uploads',
      description: 'Upload videos directly to NoteGo for transcription. Supports MP4 and WebM formats.',
      steps: [
        'Choose an MP4 or WebM file from your computer.',
        "Check the preview to make sure it's the right video.",
        'Upload it — NoteGo transcribes it like any other recording.',
      ],
      actionLabel: 'Upload a video',
      route: RouteMap.BETA_VIDEO_UPLOAD,
      icon: <VideoLibraryIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (planBetaFeatures.aboutMeGuidance) {
    betaFeatures.push({
      id: 'model-guidance',
      title: 'Custom Guidance',
      description:
        'Share a short overview about yourself to help guide the NoteGo models when responding to your prompts and notes.',
      steps: [
        'Write a few sentences about your role and the kind of notes you make.',
        'Save your guidance.',
        'Future AI answers and notes use it as context.',
      ],
      actionLabel: 'Write your guidance',
      route: RouteMap.BETA_MODEL_GUIDANCE,
      icon: <PersonOutlineIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (planBetaFeatures.screenRecording) {
    betaFeatures.push({
      id: 'screen-recording',
      title: 'Screen Recording',
      description: 'Record your screen and microphone right from your browser, then upload the session as a new note.',
      steps: [
        'Choose whether to include microphone audio.',
        'Pick the screen, window, or tab to record.',
        "Stop when you're done to upload the recording.",
      ],
      actionLabel: 'Start recording',
      route: RouteMap.BETA_SCREEN_RECORDING,
      icon: <ScreenShareIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (SECOND_BRAIN_FEATURE_ENABLED && planBetaFeatures.secondBrain) {
    betaFeatures.push({
      id: 'second-brain',
      title: 'Second Brain',
      description: 'Select tags, pull in your latest notes, and chat using your own context for smarter answers.',
      steps: [
        'Scope the chat by tag, or leave it open to search all your notes.',
        'Ask a question in plain language.',
        'Open the notes each answer cites to check the source.',
      ],
      actionLabel: 'Open Second Brain',
      route: RouteMap.BETA_SECOND_BRAIN,
      icon: <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (VIBE_CODE_FEATURE_ENABLED && planBetaFeatures.vibeCode) {
    betaFeatures.push({
      id: 'vibe-code',
      title: 'Vibe Code',
      description:
        'Select a video note, describe what you want to build, and get an AI-generated vibe coding prompt powered by Gemini video analysis.',
      steps: [
        'Pick a video note to work from.',
        'Describe what you want to build.',
        'Copy the generated prompt into your AI coding tool.',
      ],
      actionLabel: 'Open Vibe Code',
      route: RouteMap.BETA_VIBE_CODE,
      icon: <AutoFixHighIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (ZAPIER_FEATURE_ENABLED && planBetaFeatures.zapier) {
    betaFeatures.push({
      id: 'zapier',
      title: 'Zapier Integration',
      description:
        'Connect NoteGo to 5,000+ apps. Enter your Zapier webhook URL and send finalized notes to your automations.',
      steps: [
        'Create a Zap with the "Webhooks by Zapier" trigger and copy its URL.',
        'Paste the URL into the Zapier Webhook URL field and save.',
        'Use Send to Zapier on any finished note.',
      ],
      actionLabel: 'Connect Zapier',
      route: RouteMap.BETA_ZAPIER,
      icon: <WebhookIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (SMART_TAGS_FEATURE_ENABLED && planBetaFeatures.smartTags) {
    betaFeatures.push({
      id: 'smart-tags',
      title: 'Smart Tags',
      description:
        'Use AI to analyze your tags and suggest intelligent groupings. Automatically merge similar or duplicate tags with one click.',
      steps: [
        'Click "Group with AI" to review your tags (you need at least two).',
        'Look over each suggested merge.',
        'Merge the ones that fit and dismiss the rest.',
      ],
      actionLabel: 'Review tags',
      route: RouteMap.BETA_SMART_TAGS,
      icon: <LocalOfferIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (BETA_FOLDERS_FEATURE_ENABLED && planBetaFeatures.folders) {
    betaFeatures.push({
      id: 'folders',
      title: 'Folders',
      description:
        'Group related tags into folders and instantly surface every note connected to those tags—perfect for organizing initiatives during the beta.',
      steps: [
        'Create a folder and name it.',
        'Add the tags that belong together.',
        'Open the folder to see every note with those tags.',
      ],
      actionLabel: 'Open Folders',
      route: RouteMap.FOLDERS,
      icon: <FolderSpecialIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  if (BETA_ORGANIZATIONS_FEATURE_ENABLED && planBetaFeatures.organizations) {
    betaFeatures.push({
      id: 'teams',
      title: 'Teams',
      description:
        'Create a team to share notes with colleagues. Add members by email and they can open every note shared with the team.',
      steps: [
        'Create your team and give it a name.',
        'Add members by email.',
        'Use "Move to team" on any of your notes to share it with the team.',
      ],
      actionLabel: 'Open Teams',
      route: RouteMap.ORGANIZATIONS,
      icon: <GroupsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  // Note Formats lives inside the note page, so it gets a card to show testers where to look.
  // The card follows the same gate the note page uses for the feature.
  if (hasBetaFeature(userData, planBetaFeatures, 'noteFormats')) {
    betaFeatures.push({
      id: 'note-formats',
      title: 'Note Formats',
      description:
        'Turn any note into a different write-up, like a summary or a list of action items, without recording it again.',
      steps: [
        'Open one of your notes.',
        'Select the Formats tab next to Key Points and Comments.',
        'Pick a format under "Generate a new format" — reopen it anytime from "Generated formats".',
      ],
      actionLabel: 'Go to My Notes',
      route: RouteMap.MY_NOTES,
      location: 'Inside your notes',
      icon: <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      status: 'active',
    });
  }

  const handleFeatureClick = (feature: BetaFeature) => {
    if (feature.status === 'active') {
      navigate(feature.route);
    }
  };

  return (
    <Box
      px={{ xs: '16px', sm: '32px' }}
      py="24px"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Beta Features
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          You've been selected to try these features before everyone else. Each card explains what it does and how to
          try it. Some have their own page, others show up inside your notes.
        </Typography>
      </Box>

      {isPlanLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      <Box
        sx={{
          display: isPlanLoading ? 'none' : 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {betaFeatures.map((feature) => (
          <Card
            key={feature.id}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:has(.MuiCardActionArea-root:hover, .MuiButton-root:hover)':
                feature.status === 'active'
                  ? {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  : {},
              opacity: feature.status === 'coming-soon' ? 0.6 : 1,
            }}
          >
            <CardActionArea
              onClick={() => handleFeatureClick(feature)}
              disabled={feature.status === 'coming-soon'}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
              }}
            >
              <CardContent sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      bgcolor: 'neutral.lightBackground',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {feature.title}
                    </Typography>
                    {(feature.location || feature.status === 'coming-soon') && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        {feature.location && <Chip label={feature.location} size="small" variant="outlined" />}
                        {feature.status === 'coming-soon' && (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            sx={{
                              bgcolor: 'warning.light',
                              color: 'warning.contrastText',
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  {feature.description}
                </Typography>
              </CardContent>
            </CardActionArea>

            {/* Steps sit outside CardActionArea — a list inside its <button> is invalid and gets flattened by screen readers */}
            <CardContent sx={{ pt: 0, flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                How to try it
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {feature.steps.map((step) => (
                  <Typography
                    key={step}
                    component="li"
                    variant="caption"
                    sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                  >
                    {step}
                  </Typography>
                ))}
              </Box>
            </CardContent>

            <Box sx={{ px: 1, pb: 1 }}>
              <Button
                onClick={() => handleFeatureClick(feature)}
                disabled={feature.status === 'coming-soon'}
                endIcon={<ArrowForwardIcon />}
                sx={{ fontWeight: 600 }}
              >
                {feature.actionLabel}
              </Button>
            </Box>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          mt: 4,
          p: 2.5,
          borderRadius: 2,
          bgcolor: 'neutral.lightBackground',
          border: '1px solid',
          borderColor: 'neutral.otherBorder',
        }}
      >
        <Typography variant="body2" sx={{ color: 'neutral.greyFont', fontWeight: 500 }}>
          Have feedback on beta features?{' '}
          <Link
            component="button"
            variant="body2"
            sx={{ fontWeight: 500, verticalAlign: 'baseline' }}
            onClick={() => navigate(RouteMap.SUPPORT)}
          >
            Contact support
          </Link>{' '}
          and mention you're a beta tester.
        </Typography>
      </Box>
    </Box>
  );
};

export default Beta;
